import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { TableTabContaier } from "./styles";
import { Box } from "@mui/material";
import {
  assignCareOpportunity,
  getCareOpportunityList,
  getCareOpportunitySummary,
  getPracticeUsers,
  updateCareOpportunity,
} from "apiHandler/actions/clinical";
import { useUserData } from "components/customHooks/userDataContext";
import {
  CareOppTabs,
  EditModal,
  TableTabs,
} from "components/clinical/components";

import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { DataGridCustomPagination } from "components/helpers";
import {
  careGapColumns,
  careProgramsColumns,
  depressionColumns,
  emergencyColumns,
  hypertensionColumns,
  transitionOfCareColumns,
  wellnessColumns,
} from "./columnsToMap";

import dayjs from "dayjs";

const tabData = [
  { label: "Open", count: 0 },
  { label: "In-Progress", count: 0 },
  { label: "Closed", count: 0 },
];

const CareOpportunity = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [activeTab, setActiveTab] = useState(0);
  const [displayedData, setDisplayedData] = useState([]);
  const [allTabs, setAllTabs] = useState([]);
  const [tabs, setTabs] = useState([]);
  const [loadingTabs, setLoadingTabs] = useState(true);
  const [loadingTable, setLoadingTable] = useState(true);
  const [loading, setLoading] = useState(false);
  const [practices, setPractices] = useState([]);
  const [gridColumns, setGridColumns] = useState([]);
  const [tableTabs, setTableTabs] = useState(tabData);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState("");

  const [filteredData, setFilteredData] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [status, setStatus] = useState(selectedRow?.status || "");
  const [outreachStatus, setOutreachStatus] = useState(
    selectedRow?.outreach_status || ""
  );
  const [aptDate, setAptDate] = useState(null);
  const [aptTime, setAptTime] = useState(null);
  const location = useLocation();
  const userData = useUserData();
  const token = userData?.access_token;
  const name = userData.first_name + " " + userData.last_name;

  useEffect(() => {
    // Update filtered data when search query or displayed data changes
    setFilteredData(
      displayedData.filter((item) =>
        item.patient_name?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    );
  }, [searchQuery, displayedData]);

  useEffect(() => {
    getTabs();
    getPractices();
  }, []);

  const getPractices = async () => {
    try {
      const payload = {
        practice_id: `${userData.selected_practice}`,
      };
      const res = await getPracticeUsers(payload, token);
      if (res) {
        setPractices(res);
      }
    } catch (error) {
      console.log("error: ", error);
    }
  };

  const handleOnEditClick = (row) => {
    setSelectedRow(row);
    setStatus(row?.status);
    setOutreachStatus(row?.outreach_status);
    setAptTime(row?.next_appt_time);
    setAptDate(row?.next_appt_date);
  };

  const handleAssignUser = async (row) => {
    try {
      const payload = {
        type: "CareOpportunity",
        practice_id: `${userData.selected_practice}`,
        care_oppt_id: row?.care_oppt_id,
        assigned_user_name: name,
      };
      if (aptDate) {
        payload.next_appt_date = dayjs(aptDate).format("DD/MM/YYYY");
      }
      if (aptTime) {
        payload.next_appt_time = aptTime;
      }
      await updateCareOpportunity(payload, token);
    } catch (error) {
      console.log("error: ", error);
    } finally {
      console.log("tabs: ", tabs);
      getCareOppList({
        category:
          row?.care_oppt_category || tabs[activeIndex].care_oppt_category,
      });
    }
  };

  const getCareOppList = async ({ category = "", status }) => {
    setLoadingTable(true);
    setDisplayedData([]);
    try {
      const payload = {
        type: "CareOpportunity",
        practice_id: userData?.selected_practice,
        status: status || tableTabs[activeTab].label,
        care_oppt_category: category,
      };

      console.log("payload: ", payload);
      const res = await getCareOpportunityList(payload, token);

      switch (category) {
        case "Hypertension":
          setGridColumns(
            hypertensionColumns(handleOnEditClick, handleAssignUser)
          );
          break;
        case "Wellness":
          setGridColumns(wellnessColumns(handleOnEditClick, handleAssignUser));
          break;
        case "Care Programs":
          setGridColumns(
            careProgramsColumns(handleOnEditClick, handleAssignUser)
          );
          break;
        case "Transition of Care":
          setGridColumns(
            transitionOfCareColumns(handleOnEditClick, handleAssignUser)
          );
          break;
        case "Care Gaps":
          setGridColumns(careGapColumns(handleOnEditClick, handleAssignUser));
          break;
        case "Depression":
          setGridColumns(
            depressionColumns(handleOnEditClick, handleAssignUser)
          );
          break;
        case "Emergency":
          setGridColumns(emergencyColumns(handleOnEditClick, handleAssignUser));
          break;
        default:
          setGridColumns(careGapColumns(handleOnEditClick, handleAssignUser));
          break;
      }

      setDisplayedData(res);
    } catch (error) {
      console.log("error: ", error);
    } finally {
      setLoadingTable(false);
    }
  };

  const getTabs = async () => {
    setLoadingTabs(true);
    try {
      const payload = {
        type: "CareOpportunity",
        practice_id: userData?.selected_practice,
      };
      const res = await getCareOpportunitySummary(payload, token);
      if (res && res.length > 0) {
        const filteredTabs = res.filter((tab) => tab.status !== "Closed");

        const combinedTabs = filteredTabs.reduce((acc, tab) => {
          const existingTab = acc.find(
            (item) => item.care_oppt_category === tab.care_oppt_category
          );
          if (existingTab) {
            existingTab.count += tab.count;
          } else {
            acc.push({ ...tab });
          }
          return acc;
        }, []);

        const sortedTabs = combinedTabs.sort((a, b) =>
          a.care_oppt_category.localeCompare(b.care_oppt_category)
        );

        setTabs(sortedTabs);

        const { title } = location.state || {};
        const index = combinedTabs.findIndex(
          (tab) => tab.care_oppt_category === title
        );

        if (index !== -1) {
          await getCareOppList({
            category: combinedTabs[index].care_oppt_category,
            status: "Open",
          });
          updateTableTabs({ res, index });
          setActiveIndex(index);
        } else {
          await getCareOppList({
            category: combinedTabs[0].care_oppt_category,
            status: "Open",
          });
          updateTableTabs({ res });
        }

        setAllTabs(res);
      }
    } catch (error) {
      console.log("error: ", error);
    } finally {
      setLoadingTabs(false); // Set loading state to false after tabs are fetched
    }
  };

  const updateTableTabs = ({ res, index, label }) => {
    console.log("index: ", index);
    let activeTabLabel = label || res[index || activeIndex]?.care_oppt_category;

    const countMap = {
      Open: 0,
      "In-Progress": 0,
      Closed: 0,
    };

    (res || allTabs).forEach((tab) => {
      if (tab.care_oppt_category === activeTabLabel) {
        countMap[tab.status] += tab.count;
      }
    });

    const updatedTabs = tableTabs.map((tab) => ({
      ...tab,
      count: countMap[tab.label] || 0,
    }));

    setTableTabs(updatedTabs);
  };

  const handleTabChange = (index, label) => {
    setActiveTab(0);
    setActiveIndex(index);
    updateTableTabs({ index, label });
    getCareOppList({ category: label, status: "Open" });
  };

  const handleActiveTab = (index, tab) => {
    setActiveTab(index);
    getCareOppList({
      status: tab,
      category: tabs[activeIndex].care_oppt_category,
    });
  };

  const onSearch = (query) => {
    setSearchQuery(query);
  };

  const onClose = () => {
    setSelectedRow(null);
    setStatus("");
    setOutreachStatus("");
    setAptDate("");
    setAptTime("");
  };

  const onSave = async () => {
    setLoading(true);
    try {
      const payload = {
        type: "CareOpportunity",
        practice_id: `${userData.selected_practice}`,
        care_oppt_id: selectedRow?.care_oppt_id,
        status: status,
        outreach_status: outreachStatus,
        assigned_user_name: selectedRow?.assigned_user_name || name,
        user_name: name,
      };
      if (aptDate) {
        payload.next_appt_date = dayjs(aptDate).format("DD/MM/YYYY");
      }
      if (aptTime) {
        payload.next_appt_time = aptTime;
      }
      await updateCareOpportunity(payload, token);
    } catch (error) {
      console.log("error: ", error);
    } finally {
      setLoading(false);
      onClose();
      getCareOppList({
        category: tabs[activeIndex].care_oppt_category,
      });
    }
  };

  const handleSelectionChange = (newSelection) => {
    setSelectedRows(newSelection);
  };

  const handleAssign = async () => {
    try {
      const payload = {
        type: "CareOpportunity",
        practice_id: `${userData.selected_practice}`,
        assigned_user_name: selectedUser,
        care_oppt_id_list: selectedRows,
        user_name: name,
      };
      await assignCareOpportunity(payload, token);
    } catch (error) {
      console.log("error: ", error);
    } finally {
      setSelectedUser("");
      getCareOppList({
        category: tabs[activeIndex].care_oppt_category,
      });
    }
  };

  return (
    <Box>
      <CareOppTabs
        tabs={tabs}
        activeIndex={activeIndex}
        handleTabChange={handleTabChange}
        loadingTabs={loadingTabs}
      />

      <TableTabContaier>
        <TableTabs
          tableTabs={tableTabs}
          activeTab={activeTab}
          handleActiveTab={handleActiveTab}
          onSearch={onSearch}
          users={practices}
          selectedUser={selectedUser}
          showAssign={selectedRows.length > 0}
          setSelectedUser={setSelectedUser}
          handleAssign={handleAssign}
        />
        <DataGrid
          pageSize={10}
          density="compact"
          rows={filteredData}
          columns={gridColumns}
          loading={loadingTable}
          autoHeight
          pagination
          slotProps={{
            loadingOverlay: {
              variant: "linear-progress",
              noRowsVariant: "linear-progress",
            },
          }}
          getRowId={(row) => row.care_oppt_id}
          slots={{
            pagination: DataGridCustomPagination,
            toolbar: GridToolbar,
          }}
          checkboxSelection
          onRowSelectionModelChange={handleSelectionChange}
          rowSelectionModel={selectedRows}
          disableRowSelectionOnClick
        />
      </TableTabContaier>
      <EditModal
        selectedRow={selectedRow}
        loading={loading}
        loadingTable={loadingTable}
        setAptDate={setAptDate}
        setAptTime={setAptTime}
        setOutreachStatus={setOutreachStatus}
        setStatus={setStatus}
        status={status}
        onClose={onClose}
        onSave={onSave}
        aptDate={aptDate}
        aptTime={aptTime}
        gridColumns={gridColumns}
        outreachStatus={outreachStatus}
      />
    </Box>
  );
};

export default CareOpportunity;
